<script setup lang="ts">
import { ButtonQC } from '@qicgroupdoha/qic-connect-ds';
import { computed, onMounted, ref, watch } from 'vue';

import { useDeviceDetector } from '@/shared/composables';
import { CrossIcon } from '@/shared/ui/icons';

interface BaseModalProps {
  showCloseButton?: boolean;
  isOpen?: boolean;
  isRtl?: boolean;
  useButton?: boolean;
  btnText?: string;
}
interface Events {
  (event: 'close'): void;
}

const props = withDefaults(defineProps<BaseModalProps>(), {
  showCloseButton: true,
  isOpen: false,
  useButton: false,
  btnText: 'Close',
  isRtl: false,
});

const emit = defineEmits<Events>();

const modalRef = ref<HTMLDialogElement | null>(null);
const modalContentRef = ref<HTMLDivElement | null>(null);
const disableContentScroll = ref(false);
const { isMobile } = useDeviceDetector();

let startTouchY = 0;
let currentTouchY = 0;
let touchTranslateY = 0;
const isSwiping = ref(false);
const verticalOffset = ref<number>(0);

watch(
  () => props.isOpen,
  (isModalOpen) => {
    if (isModalOpen) {
      openModal();
    } else {
      onClose();
    }
  },
);

function handleOutsideClick(target: EventTarget | null) {
  const clickedOutside = target === modalRef.value;
  if (clickedOutside) {
    onClose();
  }
}

function onModalClick(event: MouseEvent) {
  handleOutsideClick(event.target);
}

function disableBodyScrolling(disable = true) {
  document.body.classList.toggle('no-scroll', disable);
}

function openModal() {
  disableBodyScrolling();
  resetModalState();
  modalRef.value?.showModal();
  if (modalContentRef.value) {
    modalContentRef.value.scrollTo({ top: 0 });
  }
}

function onClose() {
  disableBodyScrolling(false);
  modalRef.value?.close();
  emit('close');
}

function resetModalState() {
  startTouchY = 0;
  currentTouchY = 0;
  touchTranslateY = 0;
  if (modalContentRef.value && isMobile.value) {
    verticalOffset.value = 0;
    isSwiping.value = false;
  }
}

function onTouchStartHandler(event: TouchEvent) {
  if (!isMobile.value) return;

  startTouchY = event.touches[0].clientY;
  touchTranslateY = 0;
  if (modalContentRef.value) {
    isSwiping.value = false;
  }
}

function onTouchMoveHandler(event: TouchEvent) {
  if (!isMobile.value) return;
  if (modalContentRef.value?.scrollTop !== 0) return;

  currentTouchY = event.touches[0].clientY;
  const distance = currentTouchY - startTouchY;
  if (distance > 0 && modalContentRef.value) {
    disableContentScroll.value = true;
    touchTranslateY = distance;
    verticalOffset.value = touchTranslateY;
  }
}

function onTouchEndHandler() {
  if (!isMobile.value || !modalContentRef.value) return;

  disableContentScroll.value = false;
  const contentHeight = modalContentRef.value.clientHeight;
  const swipeThreshold = contentHeight * 0.5;
  if (touchTranslateY > swipeThreshold) {
    onClose();
  } else {
    isSwiping.value = true;
    verticalOffset.value = 0;
  }
}

const computedTranslateStyle = computed(() => {
  const defaultTransformForDesktop = 'translate(-50%, -50%)';
  return isMobile.value ? `translateY(${verticalOffset.value}px)` : defaultTransformForDesktop;
});

onMounted(() => {
  if (props.isOpen) {
    openModal();
  }
});
</script>

<template>
  <dialog ref="modalRef" class="modal" @close="onClose" @click="onModalClick">
    <div
      ref="modalContentRef"
      class="modal__content" :class="[
        {
          'modal__content--animate': isSwiping,
          'no-scroll': disableContentScroll,
        },
      ]"
      :style="{ transform: computedTranslateStyle }"
      @touchstart="onTouchStartHandler"
      @touchmove="onTouchMoveHandler"
      @touchend="onTouchEndHandler"
    >
      <div v-if="showCloseButton" class="modal__close-icon-wrapper">
        <CrossIcon class="modal__close-icon" @click="onClose" />
      </div>
      <slot />
      <slot v-if="useButton" name="button" v-bind="{ onClose }">
        <ButtonQC variant="secondary" size="medium" icon="none" @click.prevent.stop="onClose">
          {{ btnText }}
        </ButtonQC>
      </slot>
    </div>
  </dialog>
</template>

<style lang="scss" scoped>
@use './BaseModal';
</style>

<style lang="scss">
// TODO: продумать, стоит ли вынести данный no-scroll
.no-scroll {
  overflow: hidden !important;
}
</style>
